import _ from 'lodash';
import queryString from 'query-string';
import axios from 'axios';
import { setGlobalErrors } from 'src/reducers/global';
import { createAction } from 'redux-actions';
import { getFormValues, formValueSelector, autofill } from 'redux-form';
import { push } from 'connected-react-router';
import { CONTRIBUTION_RATES_AND_INDUSTRY_CODE_SETTING_FORM } from 'src/constants/FormNames';
import i18next from 'i18next';

import * as Urls from 'src/constants/EndpointUrls';
import { getSelectOptions, setOptions, memoColors } from 'src/reducers/selectOptions';
import {
  convertQueryToFormFieldOfEmployees,
  convertFormToQueryFieldOfEmployees,
  convertQueryToFormFieldOfPaging,
  convertFormToQueryFieldOfPaging,
  convertFormToQueryFieldOfPays,
  convertFormToQueryFieldOfBonuses,
  convertFormToQueryFieldOfRevisions,
  convertFormToQueryFieldOfNotificationOfBaseAmountRevisions,
  convertFormToQueryFieldOfDetails,
  convertQueryToFormFieldOfPays,
  convertQueryToFormFieldOfBonuses,
  convertQueryToFormFieldOfRevisions,
  convertQueryToFormFieldOfNotificationOfBaseAmountRevisions,
  convertQueryToFormFieldOfDetails,
  convertQueryToFormFieldOfWageLedgers,
  convertFormToQueryFieldOfWageLedgers,
  convertQueryToFormFieldOfClientRoleUsers,
  convertFormToQueryFieldOfClientRoleUsers,
  convertQueryToFormFieldOfReportHealthInsuranceBonus,
  convertFormToQueryFieldOfReportHealthInsuranceBonus,
  convertQueryToFormFieldOfWithholdingReports,
  convertFormToQueryFieldOfWithholdingReports,
  convertQueryToFormFieldOfAuditLogs,
  convertFormToQueryFieldOfAuditLogs,
  convertQueryToFormFieldOfIncomeTaxDeclarations,
  convertFormToQueryFieldOfIncomeTaxDeclarations
} from 'src/utils/fieldConverters';
import { convertNumberToStrOfValueInArray } from 'src/utils/Utils';
import { serializeHttpGetParams } from 'src/utils/Http';
import { setCache } from 'src/Cache';
import { getCurrentUserInfo } from 'src/reducers/users/currentUser';

export const REDUCER_NAME = 'searchForm';

// Actions
export const SET_LOADING = 'SEARCH_FORM/SET_LOADING';
export const CLEAR_LOADING = 'SEARCH_FORM/CLEAR_LOADING';

// Action Creators
export const setLoading = createAction(SET_LOADING);
export const clearLoading = createAction(CLEAR_LOADING);

// Selectors
export const isLoading = (name, state) => _.get(state, `${REDUCER_NAME}.${name}.isLoading`, false);

// Async Actions
export const setLoadingWithName = name => dispatch => {
  dispatch(setLoading({ name }));
};
export const clearLoadingWithName = name => dispatch => {
  dispatch(clearLoading({ name }));
};

export const FORM_NAME_EMPLOYEES_SEARCH = 'employeesSearchForm';
export const FORM_NAME_ATTENDANCES_SEARCH = 'attendancesSearchForm';
export const FORM_NAME_PAYS_SEARCH = 'paysSearchForm';
export const FORM_NAME_BONUSES_SEARCH = 'bonusesSearchForm';
export const FORM_NAME_REVISION_MONTHS_SEARCH = 'revisionMonthSearchForm';
export const FORM_NAME_NOTIFICATION_OF_BASE_AMOUNT_MONTHS_SEARCH = 'notificationOfBaseAmountMonthSearchForm';
export const FORM_NAME_WAGE_LEDGER_SEARCH = 'wageLedgerSearchForm';
export const FORM_NAME_ROLE_USERS_SEARCH = 'formNameRoleUsersSearch';
export const FORM_NAME_REPORT_HEALTH_INSURANCE_BONUS_SEARCH = 'formNnameReportHealthInsuranceBonusSearchForm';
export const FORM_NAME_PND_SEARCH = 'pndSearchForm';
export const FORM_NAME_PND_KOR_SEARCH = 'pndKorSearchForm';
export const FORM_NAME_TAX_DEDUCTION_CERT_SEARCH = 'taxDeductionCertSearchForm';
export const FORM_NAME_SOCIAL_SECURITY_OFFICE_SEARCH = 'socialSecurityOfficeSearchForm';
export const FORM_NAME_KT20_KOR_SEARCH = 'kt20KorSearchForm';
export const FORM_NAME_WITHHOLDING_REPORT_SEARCH = 'withholdingReportSearchForm';
export const FORM_NAME_AUDIT_LOGS_SEARCH = 'auditLogsSearchForm';
export const FORM_NAME_INCOME_TAX_DECLARATIONS_SEARCH = 'incomeTaxDeclarationSearchForm';
const DEFAULT_LIMIT = '100';
export const LIMITS = ['10', '20', '50', '100', '200'].map(ele => ({ value: ele, label: i18next.t('jbc-front.components.SearchForm.show', { count: ele }) }));

export const employeesSearchFormSelector = formValueSelector(FORM_NAME_EMPLOYEES_SEARCH);
export const attendancesSearchFormSelector = formValueSelector(FORM_NAME_ATTENDANCES_SEARCH);
export const paysSearchFormSelector = formValueSelector(FORM_NAME_PAYS_SEARCH);
export const bonusesSearchFormSelector = formValueSelector(FORM_NAME_BONUSES_SEARCH);
export const revisionMonthsSearchFormSelector = formValueSelector(FORM_NAME_REVISION_MONTHS_SEARCH);
export const notificationOfBaseAmountMonthsSearchFormSelector = formValueSelector(FORM_NAME_NOTIFICATION_OF_BASE_AMOUNT_MONTHS_SEARCH);
export const pndSearchFormSelector = formValueSelector(FORM_NAME_PND_SEARCH);
export const pndKorSearchFormSelector = formValueSelector(FORM_NAME_PND_KOR_SEARCH);
export const taxDeductionCertSearchFormSelector = formValueSelector(FORM_NAME_TAX_DEDUCTION_CERT_SEARCH);

export const getPageLimit = searchForm => {
  if (window.localStorage) {
    const limit = window.localStorage.getItem(`${searchForm}_PAGE_LIMIT`);
    if (limit !== null) {
      return limit;
    }
  }
  return DEFAULT_LIMIT;
};

export const cachePageLimit = (formName, limit) => {
  setCache(`${formName}_PAGE_LIMIT`, limit);
};

const convertFields = (fields, converter) =>
  Object.assign(
    {},
    ...Object.entries(fields)
      .map(([k, v]) => converter(k, v))
      .filter(_.identity)
  );

const takeQueries = (formQueries, converters) =>
  formQueries !== undefined ? converters.reduce((accumulator, converter) => Object.assign({}, accumulator, convertFields(formQueries, converter)), {}) : {};

const changeLocation = (formQueries, converters, dispatch, state) => {
  const search = queryString.stringify(
    converters.reduce((accumulator, converter) => Object.assign({}, accumulator, convertFields(formQueries, converter)), {})
  );
  dispatch(push({ ...state.router.location, search }));
};

export const takeEmployeesSearchQueriesFromLocation = location => {
  const converters = [convertQueryToFormFieldOfEmployees, convertQueryToFormFieldOfPaging, convertQueryToFormFieldOfDetails];
  return takeQueries(
    {
      ...{
        enrollment_status: '2',
        sort: 'staffCode',
        order: 'asc',
        page: '1',
        per_page: getPageLimit(FORM_NAME_EMPLOYEES_SEARCH)
      },
      ...{
        memo_color_ids: memoColors.map(color => color.value).join('_'),
        no_memo: '1'
      },
      ...queryString.parse(location.search)
    },
    converters
  );
};

export const takePaysSearchQueriesFromLocation = (formName, location, detailSearch = false) => {
  const converters = detailSearch ? [convertQueryToFormFieldOfPays, convertQueryToFormFieldOfDetails] : [convertQueryToFormFieldOfPays];
  return takeQueries(
    {
      ...{
        sort: 'staffCode',
        order: 'asc',
        page: '1',
        per_page: getPageLimit(formName)
      },
      ...(detailSearch
        ? {
            memo_color_ids: memoColors.map(color => color.value).join('_'),
            no_memo: '1'
          }
        : undefined),
      ...queryString.parse(location.search)
    },
    converters
  );
};

export const takeBonusesSearchQueriesFromLocation = (location, detailSearch = false) => {
  const converters = detailSearch ? [convertQueryToFormFieldOfBonuses, convertQueryToFormFieldOfDetails] : [convertQueryToFormFieldOfBonuses];
  return takeQueries(
    {
      ...{
        sort: 'staffCode',
        order: 'asc',
        page: '1',
        per_page: getPageLimit(FORM_NAME_BONUSES_SEARCH)
      },
      ...(detailSearch
        ? {
            memo_color_ids: memoColors.map(color => color.value).join('_'),
            no_memo: '1'
          }
        : undefined),
      ...queryString.parse(location.search)
    },
    converters
  );
};

export const takeRevisionsSearchQueriesFromLocation = location =>
  takeQueries(
    {
      ...{
        sort_type: 'staff_code',
        filter_type: 'only_targets',
        page: '1',
        per_page: getPageLimit(FORM_NAME_REVISION_MONTHS_SEARCH)
      },
      ...queryString.parse(location.search)
    },
    [convertQueryToFormFieldOfRevisions]
  );

export const takeNotificationOfBaseAmountSearchQueriesFromLocation = location =>
  takeQueries(
    {
      ...{
        sort_type: 'staff_code',
        filter_type: 'only_targets',
        page: '1',
        per_page: getPageLimit(FORM_NAME_NOTIFICATION_OF_BASE_AMOUNT_MONTHS_SEARCH)
      },
      ...queryString.parse(location.search)
    },
    [convertQueryToFormFieldOfNotificationOfBaseAmountRevisions]
  );

export const takeClientRoleUsersSearchQueriesFromLocation = location => {
  const converters = [convertQueryToFormFieldOfClientRoleUsers, convertQueryToFormFieldOfPaging, convertQueryToFormFieldOfDetails];
  return takeQueries(
    {
      ...{
        sort: 'staffCode',
        order: 'asc',
        page: '1',
        per_page: getPageLimit(FORM_NAME_ROLE_USERS_SEARCH)
      },
      ...{
        memo_color_ids: memoColors.map(color => color.value).join('_'),
        no_memo: '1'
      },
      ...queryString.parse(location.search)
    },
    converters
  );
};

export const takeIncomeTaxDeclarationsSearchQueriesFromLocation = location => {
  const converters = [convertQueryToFormFieldOfIncomeTaxDeclarations, convertQueryToFormFieldOfPaging];
  return takeQueries(
    {
      ...{
        page: '1',
        per_page: getPageLimit(FORM_NAME_INCOME_TAX_DECLARATIONS_SEARCH)
      },
      ...queryString.parse(location.search)
    },
    converters
  );
};

export const changeEmployeesSearchLocation = (formQueries, detailsSearch = true) => (dispatch, getState) => {
  const converters = detailsSearch
    ? [convertFormToQueryFieldOfEmployees, convertFormToQueryFieldOfPaging, convertFormToQueryFieldOfDetails]
    : [convertFormToQueryFieldOfEmployees, convertFormToQueryFieldOfPaging];
  return changeLocation(formQueries, converters, dispatch, getState());
};

export const takeWageLedgersSearchQueriesFromLocation = location => {
  const converters = [convertQueryToFormFieldOfWageLedgers, convertQueryToFormFieldOfDetails];

  return takeQueries(
    {
      ...{
        sort: 'staffCode',
        order: 'asc',
        page: '1',
        per_page: getPageLimit(FORM_NAME_WAGE_LEDGER_SEARCH)
      },
      ...{
        memo_color_ids: memoColors.map(color => color.value).join('_'),
        no_memo: '1'
      },
      ...queryString.parse(location.search)
    },
    converters
  );
};

export const takeWithholdingReportsSearchQueriesFromLocaion = (formName, location) => {
  const converters = [convertQueryToFormFieldOfWithholdingReports, convertQueryToFormFieldOfDetails];
  return takeQueries(
    {
      ...{
        sort: 'staffCode',
        order: 'asc',
        page: '1',
        per_page: getPageLimit(formName)
      },
      ...{
        memo_color_ids: memoColors.map(color => color.value).join('_'),
        no_memo: '1'
      },
      ...queryString.parse(location.search)
    },
    converters
  );
};

const takeContributionRateAndIndustryCode = state => {
  const formValues = getFormValues(CONTRIBUTION_RATES_AND_INDUSTRY_CODE_SETTING_FORM)(state);
  return formValues || { contributionRates: '', industryCode: '' };
};

export const changePaysSearchLocation = (formQueries, detailsSearch = true) => (dispatch, getState) => {
  const converters = detailsSearch ? [convertFormToQueryFieldOfPays, convertFormToQueryFieldOfDetails] : [convertFormToQueryFieldOfPays];
  return changeLocation(formQueries, converters, dispatch, getState());
};

export const changeBonusesLocation = (formQueries, detailsSearch = true) => (dispatch, getState) => {
  const converters = detailsSearch ? [convertFormToQueryFieldOfBonuses, convertFormToQueryFieldOfDetails] : [convertFormToQueryFieldOfBonuses];
  return changeLocation(formQueries, converters, dispatch, getState());
};

export const changeRevisionsLocation = formQueries => (dispatch, getState) =>
  changeLocation(formQueries, [convertFormToQueryFieldOfRevisions], dispatch, getState());

export const changeNotificationOfBaseAmountRevisionsLocation = formQueries => (dispatch, getState) =>
  changeLocation(formQueries, [convertFormToQueryFieldOfNotificationOfBaseAmountRevisions], dispatch, getState());

export const changeWageLedgersLocation = formQueries => (dispatch, getState) =>
  changeLocation(formQueries, [convertFormToQueryFieldOfWageLedgers, convertFormToQueryFieldOfDetails], dispatch, getState());
export const changeClientRolesLocation = formQueries => (dispatch, getState) =>
  changeLocation(
    formQueries,
    [convertFormToQueryFieldOfClientRoleUsers, convertFormToQueryFieldOfDetails, convertFormToQueryFieldOfPaging],
    dispatch,
    getState()
  );

export const changeWithholdingReportsLocation = formQueries => (dispatch, getState) =>
  changeLocation(formQueries, [convertFormToQueryFieldOfWithholdingReports, convertFormToQueryFieldOfDetails], dispatch, getState());

export const changeIncomeTaxDeclarationsLocation = formQueries => (dispatch, getState) =>
  changeLocation(formQueries, [convertFormToQueryFieldOfIncomeTaxDeclarations, convertFormToQueryFieldOfPaging], dispatch, getState());

export const takePaysSearchQueries = formQueries => takeQueries(formQueries, [convertFormToQueryFieldOfPays, convertFormToQueryFieldOfDetails]);

export const takePaysSearchQueriesWithoutDetails = formQueries => takeQueries(formQueries, [convertFormToQueryFieldOfPays]);

export const takeBonusesSearchQueries = formQueries => takeQueries(formQueries, [convertFormToQueryFieldOfBonuses, convertFormToQueryFieldOfDetails]);

export const takeEmployeesSearchQueries = formQueries =>
  takeQueries(formQueries, [convertFormToQueryFieldOfEmployees, convertFormToQueryFieldOfDetails, convertFormToQueryFieldOfPaging]);

export const takeWithholdingReportsSearchQueries = formQueries =>
  takeQueries(formQueries, [convertFormToQueryFieldOfWithholdingReports, convertFormToQueryFieldOfDetails]);

export const takeRevisionsSearchQueries = formQueries => takeQueries(formQueries, [convertFormToQueryFieldOfRevisions]);

export const takeNotificationOfBaseAmountRevisionsSearchQueries = formQueries =>
  takeQueries(formQueries, [convertFormToQueryFieldOfNotificationOfBaseAmountRevisions]);

export const takeWageLedgersSearchQueries = formQueries => takeQueries(formQueries, [convertFormToQueryFieldOfWageLedgers, convertFormToQueryFieldOfDetails]);

export const takeClientRoleUsersSearchQueries = formQueries =>
  takeQueries(formQueries, [convertFormToQueryFieldOfClientRoleUsers, convertFormToQueryFieldOfDetails, convertFormToQueryFieldOfPaging]);

export const takeIncomeTaxDeclarationsSearchQueries = formQueries =>
  takeQueries(formQueries, [convertFormToQueryFieldOfIncomeTaxDeclarations, convertFormToQueryFieldOfDetails, convertFormToQueryFieldOfPaging]);

export const takePayConfirmedStateFromSearchForm = (state, formName) => {
  const uniquePaymentDates = getSelectOptions(state, 'uniquePaymentDates');
  const currentUniquePaymentDate = formValueSelector(formName)(state, 'uniquePaymentDate');

  if (uniquePaymentDates.length < 1 || !currentUniquePaymentDate) {
    return false;
  }

  return uniquePaymentDates.some(ele => ele.value === currentUniquePaymentDate && ele.confirmed);
};

export const takePaymentRangeFormSearchForm = (state, formName) => ({
  payrollRulesGroups: formValueSelector(formName)(state, 'uniquePayrollRuleGroups'),
  yearMonth: formValueSelector(formName)(state, 'uniquePaymentDate')
});

export const takePaymentDateFromSearchForm = (state, formName) => {
  const dates = getSelectOptions(state, 'uniquePaymentDates');
  const targetDate = formValueSelector(formName)(state, 'uniquePaymentDate');

  if (dates.length < 1 || !targetDate) {
    return undefined;
  }

  return dates.find(ele => ele.value === targetDate);
};

export const takeGroupedPaymentMonthsSearchForm = (state, formName) => ({
  groupedPaymentMonths: formValueSelector(formName)(state, 'groupedPaymentMonths')
});

export const takeOfficesSearchForm = (state, formName) => ({
  offices: formValueSelector(formName)(state, 'offices')
});

export const takeGroupedPaymentYearsSearchForm = (state, formName) => ({
  groupedPaymentYears: formValueSelector(formName)(state, 'groupedPaymentYears')
});

export const takeClientOrClientBranchSearchForm = (state, formName) => ({
  clientCompanyId: formValueSelector(formName)(state, 'clientCompanies')
});

export const takeYearSearchForm = (state, formName) => ({
  year: formValueSelector(formName)(state, 'paymentYears')
});

export const takeBonusFromSearchForm = state => {
  const bonuses = getSelectOptions(state, 'bonuses');
  const currentBonusId = bonusesSearchFormSelector(state, 'clientBonusId');

  if (bonuses.length < 1 || !currentBonusId) {
    return undefined;
  }

  return bonuses.find(ele => ele.value === currentBonusId);
};

export const takeBonusConfirmedStateFromSearchForm = state => {
  const bonus = takeBonusFromSearchForm(state);
  if (bonus !== undefined) {
    return bonus.isConfirmed;
  }
  return false;
};

export const takeAttendancesQueriedUrl = (baseUri, formQueries) => {
  const queries = takePaysSearchQueries(formQueries);
  return `${baseUri}?${serializeHttpGetParams(queries)}`;
};

export const takePaysQueriedUrl = (baseUri, formQueries, isPdf = false, extras = {}) => {
  const queries = takePaysSearchQueries(formQueries);
  Object.keys(extras).map(field => {
    queries[field] = extras[field];
    return true;
  });

  if (isPdf) {
    return `${baseUri}?${serializeHttpGetParams(queries)}&pdf%5Btype%5D=zip`;
  }
  return `${baseUri}?${serializeHttpGetParams(queries)}`;
};

export const getPayTitle = (formQueries, state) => {
  const queries = takePaysSearchQueries(formQueries);
  const payrollRulesGroups = queries.payroll_rules_groups;
  const yearMonth = queries.year_month;
  const uniquePaymentDates = getSelectOptions(state, 'uniquePaymentDates');
  const uniquePayrollRuleGroups = getSelectOptions(state, 'uniquePayrollRuleGroups');
  const ruleGroupTitle = (uniquePayrollRuleGroups.find(item => item.value === payrollRulesGroups) || {}).label || '';
  const paymentDateTitle = (uniquePaymentDates.find(item => item.value === yearMonth) || {}).label || '';
  return `${ruleGroupTitle} ${paymentDateTitle}`;
};

export const takeBonusesQueriedUrl = (baseUri, formQueries, isPdf = false) => {
  const queries = takeBonusesSearchQueries(formQueries);
  if (isPdf) {
    return `${baseUri}?${serializeHttpGetParams(queries)}&pdf%5Btype%5D=zip`;
  }
  return `${baseUri}?${serializeHttpGetParams(queries)}`;
};

export const takePaysFbDataUrl = state => {
  const queries = takePaymentRangeFormSearchForm(state, FORM_NAME_PAYS_SEARCH);
  const { yearMonth, payrollRulesGroups } = queries;
  return `${Urls.SHOW_PAY_FB_DATA_PAGE_URL}?${serializeHttpGetParams({ yearMonth, payrollRulesGroups })}`;
};

// download pnd pdf (summary, detail)
export const takePndPdfUrl = state => {
  const queries = takeGroupedPaymentMonthsSearchForm(state, FORM_NAME_PND_SEARCH);
  const query = takeOfficesSearchForm(state, FORM_NAME_PND_SEARCH);
  const params = { grouped_payment_months: queries.groupedPaymentMonths, client_branch_id: query.offices };
  return `${Urls.DOWNLOAD_PND_PDF}?${serializeHttpGetParams(params)}`;
};

// download social security office pdf
export const takeSocialSecurityOfficePdfUrl = state => {
  const getGroupedPaymentMonths = takeGroupedPaymentMonthsSearchForm(state, FORM_NAME_SOCIAL_SECURITY_OFFICE_SEARCH);
  const { groupedPaymentMonths } = getGroupedPaymentMonths;
  const getOffices = takeOfficesSearchForm(state, FORM_NAME_SOCIAL_SECURITY_OFFICE_SEARCH);
  const { offices } = getOffices;
  const params = { groupedPaymentMonths, clientOrCompanyId: offices };
  return `${Urls.DOWNLOAD_SOCIAL_SECURITY_OFFICE_PDF}?${serializeHttpGetParams(params)}`;
};

// download pnd kor pdf (summary, detail)
export const takePndKorPdfUrl = state => {
  const queries = takeGroupedPaymentYearsSearchForm(state, FORM_NAME_PND_KOR_SEARCH);
  const query = takeOfficesSearchForm(state, FORM_NAME_PND_KOR_SEARCH);
  const params = { year: queries.groupedPaymentYears, client_branch_id: query.offices };
  return `${Urls.DOWNLOAD_PND_KOR_PDF}?${serializeHttpGetParams(params)}`;
};

//
// 住民税振込一覧表
//
export const takeInhabitantTaxFbDataUrl = state => {
  const queries = takePaymentRangeFormSearchForm(state, FORM_NAME_PAYS_SEARCH);
  const { yearMonth, payrollRulesGroups } = queries;
  return `${Urls.SHOW_INHABITANT_TAX_FB_PAGE}?${serializeHttpGetParams({ yearMonth, payrollRulesGroups })}`;
};

export const takeDownloadInhabitantTaxFbDataUrl = (state, payrollGroupSelectorType) => {
  let params = {};
  if (payrollGroupSelectorType === 'all') {
    const queries = takeGroupedPaymentMonthsSearchForm(state, FORM_NAME_PAYS_SEARCH);
    const { groupedPaymentMonths } = queries;
    params = { groupedPaymentMonths };
  } else {
    const queries = takePaymentRangeFormSearchForm(state, FORM_NAME_PAYS_SEARCH);
    const { yearMonth, payrollRulesGroups } = queries;
    params = { yearMonth, payrollRulesGroups };
  }
  return `${Urls.DOWNLOAD_INHABITANT_PDF}?${serializeHttpGetParams(params)}`;
};

export const takeBonusesFbDataUrl = formQueries => {
  const clientBonusId = _.get(takeBonusesSearchQueries(formQueries), 'client_bonus_id');
  return `${Urls.SHOW_BONUS_FB_DATA_PAGE_URL}?${serializeHttpGetParams({ clientBonusId })}`;
};

export const takePaysAndAttendancesResetFormQueries = (formName, state, detailSearch = false) => {
  const uniquePayrollRuleGroups = _.get(getSelectOptions(state, 'uniquePayrollRuleGroups'), '0.value');
  // 条件解除時に支給日はリセットしない(値が設定されてないときは、リストの最新をセットする)
  const currentDate = formValueSelector(formName)(state, 'uniquePaymentDate');
  const uniquePaymentDate = currentDate || _.get(getSelectOptions(state, 'uniquePaymentDates'), '0.value');
  return {
    ...takePaysSearchQueriesFromLocation(formName, { search: '' }, detailSearch),
    uniquePayrollRuleGroups,
    uniquePaymentDate
  };
};

export const takeBonusesResetFormQueries = (state, detailSearch = false) => {
  const currentBonusId = bonusesSearchFormSelector(state, 'clientBonusId');
  // 条件解除時にボーナスIDはリセットしない(値が設定されてないときは、リストの最新をセットする)
  const clientBonusId = currentBonusId || _.get(getSelectOptions(state, 'bonuses'), '0.value');
  return {
    ...takeBonusesSearchQueriesFromLocation({ search: '' }, detailSearch),
    clientBonusId
  };
};

export const takeEmployeesResetFormQueries = (state, detailSearch = false) => takeEmployeesSearchQueriesFromLocation({ search: '' }, detailSearch);

export const takeWageLedgersResetFormQueries = (state, detailSearch = false) => {
  const year = _.get(getSelectOptions(state, 'wageLedgerYears'), '0.value');
  return {
    ...takeWageLedgersSearchQueriesFromLocation({ search: '' }, detailSearch),
    year
  };
};

export const takeWithholdingReportsResetFormQueries = (state, detailSearch = false) => {
  const year = _.get(getSelectOptions(state, 'paymentYears'), '0.value');
  const resign = false;
  return {
    ...takeWithholdingReportsSearchQueriesFromLocaion({ search: '' }, detailSearch),
    year,
    resign
  };
};

export const takeClientRoleUsersResetFormQueries = () => takeClientRoleUsersSearchQueriesFromLocation({ search: '' });

export const takeIncomeTaxDeclarationsResetFormQueries = () => takeIncomeTaxDeclarationsSearchQueriesFromLocation({ search: '' });

const makeKeyByState = (key, state) => `${key}#${state.router.location.pathname}#${getCurrentUserInfo(state).email}`;

export const saveUniquePaymentDate = value => (dispatch, getState) => {
  if (window.sessionStorage) {
    window.sessionStorage.setItem(makeKeyByState('uniquePaymentDate', getState()), value);
  }
};

export const ITEM_NAME_FETCH_UNIQUE_PAYMENT_DATES = 'fetchUniquePaymentDates';
export const fetchUniquePaymentDates = (formName, uniquePayrollRuleGroupIds, onlyConfirmed, updateLocation = false) => (dispatch, getState) => {
  dispatch(setLoadingWithName(ITEM_NAME_FETCH_UNIQUE_PAYMENT_DATES));
  axios
    .get(Urls.OPTIONS_CLIENT_UNIQUE_PAYMENT_DATE, {
      params: {
        payroll_rules_group_ids: uniquePayrollRuleGroupIds,
        ...(onlyConfirmed ? { is_confirmed: true } : undefined)
      }
    })
    .then(response => {
      const location = getState().router.location;

      dispatch(
        setOptions({
          uniquePaymentDates: response.data.payload.options
        })
      );

      if (updateLocation) {
        const uniquePaymentDate = response.data.payload.options[0]?.value;
        dispatch(autofill(formName, 'uniquePaymentDate', uniquePaymentDate));
        dispatch(changePaysSearchLocation(getFormValues(formName)(getState())));
      } else {
        // クエリから支払日をセット、なければ一番最初の支払日をセット
        let uniquePaymentDate = takePaysSearchQueriesFromLocation(formName, location).uniquePaymentDate;
        if (uniquePaymentDate === undefined && window.sessionStorage) {
          uniquePaymentDate = window.sessionStorage.getItem(makeKeyByState('uniquePaymentDate', getState()));
        }
        if (uniquePaymentDate === undefined || !response.data.payload.options.some(option => option.value === uniquePaymentDate)) {
          uniquePaymentDate = response.data.payload.options[0]?.value;
          dispatch(saveUniquePaymentDate(uniquePaymentDate));
        }
        dispatch(autofill(formName, 'uniquePaymentDate', uniquePaymentDate));
      }
    })
    .catch(error => {
      console.log(error);
    })
    .finally(() => {
      dispatch(clearLoadingWithName(ITEM_NAME_FETCH_UNIQUE_PAYMENT_DATES));
    });
};

export const saveUniquePayrollRuleGroupIds = value => (dispatch, getState) => {
  if (window.sessionStorage) {
    window.sessionStorage.setItem(makeKeyByState('uniquePayrollRuleGroups', getState()), value);
  }
};

export const setModalOpen = value => {
  if (window.sessionStorage) {
    window.sessionStorage.setItem('modalOpen', value);
  }
};

export const getModalOpen = () => {
  if (window.sessionStorage) {
    if (window.sessionStorage.getItem('modalOpen') === 'true') return true;
    else if (window.sessionStorage.getItem('modalOpen') === 'false') return false;
    return null;
  }
  return null;
};

export const removeModalOpen = () => {
  if (window.sessionStorage) {
    window.sessionStorage.removeItem('modalOpen');
  }
};

export const setLanguage = value => {
  if (window.sessionStorage) {
    window.sessionStorage.setItem('language', value);
  }
};

export const getLanguage = () => {
  if (window.sessionStorage) {
    return window.sessionStorage.getItem('language');
  }

  return '';
};

export const removeLanguage = () => {
  if (window.sessionStorage) {
    window.sessionStorage.removeItem('language');
  }
};

export const ITEM_NAME_FETCH_UNIQUE_PAYROLL_RULE_GROUPS = 'fetchUniquePayrollRuleGroups';
export const fetchUniquePayrollRuleGroups = (formName, onlyConfirmed) => async (dispatch, getState) => {
  try {
    dispatch(setLoadingWithName(ITEM_NAME_FETCH_UNIQUE_PAYROLL_RULE_GROUPS));
    const response = await axios.get(Urls.OPTIONS_CLIENT_UNIQUE_PAYROLL_RULE_GROUPS, {
      params: { ...(onlyConfirmed ? { is_confirmed: true } : undefined) }
    });

    const uniquePayrollRuleGroup = response.data.payload.options.map(payrollRuleGroup => {
      if (payrollRuleGroup.paymentCount === 'twice') {
        // TODO: The payroll rule label in the salary list is passed in the format "Payroll Rule Name [Payment Date (Cutoff Date)]".
        //       For semi-monthly payroll rules, since the label becomes too long, cut the "[Payment Date (Cutoff Date)]".
        //       Need to reconsider how to display semi-monthly payroll rules.
        const splitPayrollRuleGroupLabel = payrollRuleGroup.label.split(' [');
        return { ...payrollRuleGroup, label: splitPayrollRuleGroupLabel[0] };
      }
      return payrollRuleGroup;
    });

    const orderedUniquePayrollRuleGroup = _.orderBy(uniquePayrollRuleGroup, ['paymentCount'], 'asc');

    dispatch(setOptions({ uniquePayrollRuleGroups: orderedUniquePayrollRuleGroup }));
    // クエリから締め日支給日をセット、なければ一番最初の締め日支給日をセット
    let uniquePayrollRuleGroupIds = takePaysSearchQueriesFromLocation(formName, getState().router.location).uniquePayrollRuleGroups;

    if (uniquePayrollRuleGroupIds === undefined && window.sessionStorage) {
      uniquePayrollRuleGroupIds = window.sessionStorage.getItem(makeKeyByState('uniquePayrollRuleGroups', getState()));
    }

    if (uniquePayrollRuleGroupIds === undefined || !response.data.payload.options.some(option => option.value === uniquePayrollRuleGroupIds)) {
      uniquePayrollRuleGroupIds = response.data.payload.options[0].value;
      dispatch(saveUniquePayrollRuleGroupIds(uniquePayrollRuleGroupIds));
    }

    // uniquePayrollRuleGroupIds = 443;
    dispatch(autofill(formName, 'uniquePayrollRuleGroups', uniquePayrollRuleGroupIds));
    fetchUniquePaymentDates(formName, uniquePayrollRuleGroupIds, onlyConfirmed)(dispatch, getState);
  } catch (error) {
    console.log(error);
  } finally {
    dispatch(clearLoadingWithName(ITEM_NAME_FETCH_UNIQUE_PAYROLL_RULE_GROUPS));
  }
};

// 全社に対応したセレクトボックスのオプションを取得する
export const ITEM_NAME_FETCH_GROUPED_PAYMENT_MONTHS = 'fetchGroupedPaymentMonths';
export const fetchGroupedPaymentMonths = () => async dispatch => {
  try {
    dispatch(setLoadingWithName(ITEM_NAME_FETCH_GROUPED_PAYMENT_MONTHS));
    const response = await axios.get(Urls.OPTIONS_CLIENT_GROUPED_PAYMENT_MONTHS);
    dispatch(setOptions({ groupedPaymentMonths: response.data.payload.options }));
  } catch (_e) {
    const errorMessages = [i18next.t('reducers.searchForm.failed_to_retrieve_dropdown'), i18next.t('reducers.searchForm.please_try_to_reload_this_page')];
    dispatch(setGlobalErrors(errorMessages));
  } finally {
    dispatch(clearLoadingWithName(ITEM_NAME_FETCH_GROUPED_PAYMENT_MONTHS));
  }
};

// 全社に対応したセレクトボックスのオプションを取得する(confirmedされているかをチェックしない) PND用 & SSO用
export const ITEM_NAME_FETCH_GROUPED_PAYMENT_MONTHS_WITHOUT_CONFIRMED = 'fetchGroupedPaymentMonthsWithoutConfirmed';
export const fetchGroupedPaymentMonthsWithoutConfirmed = () => async dispatch => {
  try {
    dispatch(setLoadingWithName(ITEM_NAME_FETCH_GROUPED_PAYMENT_MONTHS_WITHOUT_CONFIRMED));
    const response = await axios.get(Urls.OPTIONS_CLIENT_GROUPED_PAYMENT_MONTHS_WITHOUT_CONFIRMED);
    dispatch(setOptions({ groupedPaymentMonths: response.data.payload.options }));
  } catch (_e) {
    const errorMessages = [i18next.t('reducers.searchForm.failed_to_retrieve_dropdown'), i18next.t('reducers.searchForm.please_try_to_reload_this_page')];
    dispatch(setGlobalErrors(errorMessages));
  } finally {
    dispatch(clearLoadingWithName(ITEM_NAME_FETCH_GROUPED_PAYMENT_MONTHS_WITHOUT_CONFIRMED));
  }
};

// PND1Kor用 全社に対応したセレクトボックスのオプションを取得する(confirmedされているかをチェックしない)
export const ITEM_NAME_FETCH_GROUPED_PAYMENT_YEARS_WITHOUT_CONFIRMED = 'fetchGroupedPaymentYearsWithoutConfirmed';
export const fetchGroupedPaymentYearsWithoutConfirmed = () => async dispatch => {
  try {
    dispatch(setLoadingWithName(ITEM_NAME_FETCH_GROUPED_PAYMENT_YEARS_WITHOUT_CONFIRMED));
    const response = await axios.get(Urls.OPTIONS_CLIENT_GROUPED_PAYMENT_YEARS_WITHOUT_CONFIRMED);
    dispatch(setOptions({ groupedPaymentYears: response.data.payload.options }));
  } catch (_e) {
    const errorMessages = [i18next.t('reducers.searchForm.failed_to_retrieve_dropdown'), i18next.t('reducers.searchForm.please_try_to_reload_this_page')];
    dispatch(setGlobalErrors(errorMessages));
  } finally {
    dispatch(clearLoadingWithName(ITEM_NAME_FETCH_GROUPED_PAYMENT_YEARS_WITHOUT_CONFIRMED));
  }
};

export const ITEM_NAME_FETCH_BONUS_OPTIONS = 'fetchBonusOptions';
export const fetchBonusOptions = (onlyConfirmed = false) => async (dispatch, getState) => {
  try {
    dispatch(setLoadingWithName(ITEM_NAME_FETCH_BONUS_OPTIONS));
    const response = await axios.get(Urls.OPTIONS_CLIENT_BONUSES, {
      params: { ...(onlyConfirmed ? { is_confirmed: true } : undefined) }
    });
    const bonuses = convertNumberToStrOfValueInArray(response.data.payload.options);
    dispatch(
      setOptions({
        bonuses
      })
    );
    // クエリから bonus_id をセット、なければ一番最初の bonus_id をセット
    let clientBonusId = takeBonusesSearchQueriesFromLocation(getState().router.location).clientBonusId;
    if (clientBonusId === undefined && bonuses.length > 0) {
      clientBonusId = bonuses[0].value;
    }
    dispatch(autofill(FORM_NAME_BONUSES_SEARCH, 'clientBonusId', clientBonusId));
  } catch (error) {
    console.log(error);
  } finally {
    dispatch(clearLoadingWithName(ITEM_NAME_FETCH_BONUS_OPTIONS));
  }
};

export const ITEM_NAME_FETCH_REVISION_MONTH_OPTIONS = 'fetchRevisionMonthOptions';
export const fetchRevisionMonthOptions = () => async (dispatch, getState) => {
  try {
    dispatch(setLoadingWithName(ITEM_NAME_FETCH_REVISION_MONTH_OPTIONS));
    const response = await axios.get(Urls.OPTIONS_CLIENT_REVISION_MONTHS);
    const revisionYearMonths = convertNumberToStrOfValueInArray(response.data.payload.options);
    dispatch(setOptions({ revisionYearMonths }));
    // クエリから revision_month_id をセット、なければ一番最初の revision_month_id をセット
    let yearMonth = takeRevisionsSearchQueriesFromLocation(getState().router.location).yearMonth;
    if (yearMonth === undefined) {
      yearMonth = revisionYearMonths[0].value;
    }
    dispatch(autofill(FORM_NAME_REVISION_MONTHS_SEARCH, 'yearMonth', yearMonth));
  } catch (error) {
    console.log(error);
  } finally {
    dispatch(clearLoadingWithName(ITEM_NAME_FETCH_REVISION_MONTH_OPTIONS));
  }
};

// 算定基礎
export const ITEM_NAME_FETCH_NOTIFICATION_OF_BASE_AMOUNT_REVISION_MONTH_OPTIONS = 'fetchNotificationOfBaseAmountMonthOptions';
export const fetchNotificationOfBaseAmountMonthOptions = () => async (dispatch, getState) => {
  try {
    dispatch(setLoadingWithName(ITEM_NAME_FETCH_NOTIFICATION_OF_BASE_AMOUNT_REVISION_MONTH_OPTIONS));
    const response = await axios.get(Urls.OPTIONS_CLIENT_NOTIFICATION_OF_BASE_AMOUNT_MONTHS);
    const revisionYears = convertNumberToStrOfValueInArray(response.data.payload.options);
    dispatch(setOptions({ revisionYears }));
    // クエリから revision_month_id をセット、なければ一番最初の revision_month_id をセット
    let yearMonth = takeNotificationOfBaseAmountSearchQueriesFromLocation(getState().router.location).yearMonth;
    if (yearMonth === undefined) {
      yearMonth = revisionYears[0].value;
    }
    dispatch(autofill(FORM_NAME_NOTIFICATION_OF_BASE_AMOUNT_MONTHS_SEARCH, 'yearMonth', yearMonth));
  } catch (error) {
    console.log(error);
  } finally {
    dispatch(clearLoadingWithName(ITEM_NAME_FETCH_NOTIFICATION_OF_BASE_AMOUNT_REVISION_MONTH_OPTIONS));
  }
};

// 賃金台帳
export const fetchWageLedgerYear = () => (dispatch, getState) => {
  axios
    .get(Urls.OPTIONS_CLIENT_WAGE_LEDGER_YEARS)
    .then(response => {
      const wageLedgerYears = convertNumberToStrOfValueInArray(response.data.payload.options);
      dispatch(
        setOptions({
          wageLedgerYears
        })
      );

      // クエリから year をセット、なければ一番最初の year をセット
      let year = takeWageLedgersSearchQueriesFromLocation(getState().router.location).year;
      if (year === undefined) {
        year = wageLedgerYears[0].value;
      }
      dispatch(autofill(FORM_NAME_WAGE_LEDGER_SEARCH, 'year', year));
    })
    .catch(error => {
      console.log(error);
    });
};

export const fetchClientRoles = () => dispatch => {
  axios
    .get(Urls.OPTIONS_CLIENT_ROLES)
    .then(response => {
      const clientRoles = convertNumberToStrOfValueInArray(response.data.payload.options);
      dispatch(
        setOptions({
          clientRoles
        })
      );
    })
    .catch(error => {
      console.log(error);
    });
};
export const convertFormToQueryString = () => ({});

export const INITIAL_STATE = {};
export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_LOADING:
      if (action.payload && action.payload.name) {
        return { ...state, [action.payload.name]: { isLoading: true } };
      }
      return state;
    case CLEAR_LOADING:
      if (action.payload && action.payload.name) {
        return { ...state, [action.payload.name]: { isLoading: false } };
      }
      return state;
    default:
      return state;
  }
};

export const takeResetFormQueries = (state, searchQueriesFromLocation, detailSearch = false) => searchQueriesFromLocation({ search: '' }, detailSearch);

export const takeReportHealthInsuranceBonusQueriesFrom = () => {
  takeQueries(
    {
      ...{
        sort: 'healthInsuranceInsuredPersonnelNumber',
        order: 'asc',
        page: '1',
        per_page: getPageLimit(FORM_NAME_REPORT_HEALTH_INSURANCE_BONUS_SEARCH)
      }
    },
    [convertQueryToFormFieldOfReportHealthInsuranceBonus]
  );
};

export const takeReportHealthInsuranceBonusSearchQueries = formQueries => takeQueries(formQueries, [convertFormToQueryFieldOfReportHealthInsuranceBonus]);

export const takeReportHealthInsuranceBonusFormQueries = (state, detailSearch = false) => {
  const bonusId = _.get(getSelectOptions(state, 'bonusNames'), '0.value');
  return {
    ...takeReportHealthInsuranceBonusQueriesFrom({}, detailSearch),
    bonusId
  };
};

export const changeReportHealthInsuranceBonusLocation = formQueries => (dispatch, getState) =>
  changeLocation(formQueries, [convertFormToQueryFieldOfReportHealthInsuranceBonus], dispatch, getState());

// KT20 Kor
export const ITEM_NAME_FETCH_PAYMENT_YEARS = 'fetchPaymentYears';
export const fetchKt20KorYear = () => async dispatch => {
  try {
    dispatch(setLoadingWithName(ITEM_NAME_FETCH_PAYMENT_YEARS));
    const response = await axios.get(Urls.OPTIONS_KT20_KOR_YEARS);
    dispatch(setOptions({ paymentYears: response.data.payload.options }));
  } catch (e) {
    dispatch(setGlobalErrors(i18next.t('reducers.searchForm.failed_to_retrieve_data')));
  }
};

// 50tawi
export const fetchWithholdingReportYear = () => async dispatch => {
  try {
    const response = await axios.get(Urls.OPTIONS_WITHHOLDING_REPORT_YEARS);
    const withholdingReportYears = convertNumberToStrOfValueInArray(response.data.payload.options);
    dispatch(setOptions({ paymentYears: withholdingReportYears }));
    dispatch(autofill(FORM_NAME_WITHHOLDING_REPORT_SEARCH, 'year', withholdingReportYears[0].value));
  } catch (e) {
    dispatch(setGlobalErrors(i18next.t('reducers.searchForm.failed_to_retrieve_data')));
  }
};

export const takeKt20KorPdfUrl = state => {
  const getClientOrClientBranch = takeClientOrClientBranchSearchForm(state, FORM_NAME_KT20_KOR_SEARCH);
  const getYear = takeYearSearchForm(state, FORM_NAME_KT20_KOR_SEARCH);
  const { clientCompanyId } = getClientOrClientBranch;
  const { year } = getYear;
  const { contributionRates, industryCode } = takeContributionRateAndIndustryCode(state);
  const params = { clientCompanyId, year, contributionRates, industryCode };
  return `${Urls.DOWNLOAD_KT20_KOR_PDF}?${serializeHttpGetParams(params)}`;
};

// AuditLogs
export const takeAuditLogsSearchQueriesFromLocation = location =>
  takeQueries(
    {
      ...{
        page: '1',
        per_page: '20'
      },
      ...queryString.parse(location.search)
    },
    [convertQueryToFormFieldOfAuditLogs]
  );

export const takeAuditLogsSearchQueries = formQueries => takeQueries(formQueries, [convertFormToQueryFieldOfAuditLogs]);

export const takeAuditLogsResetFormQueries = () => ({
  ...takeAuditLogsSearchQueriesFromLocation({ search: '' })
});

export const changeAuditLogsLocation = formQueries => (dispatch, getState) => {
  changeLocation(formQueries, [convertFormToQueryFieldOfAuditLogs], dispatch, getState());
};

export const fetchUserWithholdingReportYear = () => async dispatch => {
  try {
    const response = await axios.get(Urls.USER_FETCH_WITHHOLDING_REPORT_YEARS);
    dispatch(setOptions({ paymentYears: _.get(response, 'data', []) }));
  } catch (_error) {
    const errorMessages = [i18next.t('reducers.searchForm.failed_to_retrieve_data'), i18next.t('reducers.searchForm.please_try_to_reload_this_page')];
    dispatch(setGlobalErrors(errorMessages));
  }
};
